import { useSortable } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import { faPenToSquare } from "@elynx/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormInput from "../../../../components/forms/form-input/FormInput";
import StepMenuLandingCard from "../../../../components/step-menu/StepMenuLandingCard";
import StepMenuLandingCardAvatar from "../../../../components/step-menu/StepMenuLandingCardAvatar";
import StepMenuLandingCardHeader from "../../../../components/step-menu/StepMenuLandingCardHeader";
import ListDragHandle from "../../../list-drag-handle/ListDragHandle";
import { tagGroupIsValid } from "../ObjectTagValuesConfigurationEditorUtils";
import { ObjectTagValuesConfigurationFieldType, TagGroupFieldKeyType } from "../ObjectTagValuesConfigurationFieldTypes";

import type { JSX } from "react";

type LandingTagGroupCardProps = {
    tagGroupKey: TagGroupFieldKeyType;
    duplicateEnabled?: boolean;
    deleteEnabled?: boolean;
    onEdit?: (tagGroupKey: TagGroupFieldKeyType) => void;
    onDuplicate?: (tagGroupKey: TagGroupFieldKeyType) => void;
    onDelete?: (tagGroupKey: TagGroupFieldKeyType) => void;
};

export default function LandingTagGroupCard({
    tagGroupKey,
    duplicateEnabled,
    deleteEnabled,
    onEdit,
    onDuplicate,
    onDelete
}: LandingTagGroupCardProps): JSX.Element {
    const { t } = useTranslation();

    const {
        getValues
    } = useFormContext<ObjectTagValuesConfigurationFieldType>();

    const tagGroup = getValues(tagGroupKey);
    const isTagGroupValid = tagGroupIsValid(tagGroup);

    const {
        attributes,
        listeners,
        transform,
        transition,
        isDragging,
        setNodeRef,
        setActivatorNodeRef
    } = useSortable({ id: tagGroup.name });

    return (
        <StepMenuLandingCard style={{
            position: "relative",
            transform: CSS.Transform.toString(transform),
            transition
        }}
            isDragging={isDragging ? true : false}
            ref={setNodeRef}
        >
            <Box>
                <ListDragHandle
                    ref={setActivatorNodeRef}
                    {...listeners}
                    {...attributes}
                    sx={{
                        position: "absolute",
                        left: "50%",
                        transform: "translate(-50%, 0)"
                    }}
                />
            </Box>

            <StepMenuLandingCardHeader
                editEnabled={true}
                editCallToAction={!isTagGroupValid}
                duplicateEnabled={duplicateEnabled}
                deleteEnabled={deleteEnabled}
                onEdit={() => onEdit && onEdit(tagGroupKey)}
                onDuplicate={onDuplicate ? () => onDuplicate(tagGroupKey) : undefined}
                onDelete={onDelete ? () => onDelete(tagGroupKey) : undefined}
                title={
                    <FormInput
                        name={`${tagGroupKey}.name`}
                        label={t("objectTagValuesConfiguration.steps.landing.customTagGroup")}
                        size="small"
                    />
                }
                avatar={
                    <StepMenuLandingCardAvatar isValid={isTagGroupValid}>
                        <FontAwesomeIcon icon={faPenToSquare} size="2x" />
                    </StepMenuLandingCardAvatar>
                }
            />
        </StepMenuLandingCard>
    );
}